import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { HistoryBaseComponent } from 'src/app/history-base/history-base.component';
import { CarrierAddressItem } from 'src/models/DTO/carrier/carrierAddressItem';
import { CarrierAddressEntry } from 'src/models/DTO/carrier/carrierAddressEntry';
import { OrderInfo } from 'src/models/DTO/orders/orderInfo';
import { UserDTO } from 'src/models/DTO/user/user';
import { SpeedyOffice, SpeedyOfficeItem } from 'src/models/DTO/carrier/speedyOffice';
import { SpeedyService } from 'src/services/request/speedy/speedy.service';
import { HistoryService } from 'src/services/state/history/history.service';
import { LoggedUserService } from 'src/services/state/loggeduser/loggeduser.service';
import { OrederStateService } from 'src/services/state/order/oreder-state.service';
import { ToastService } from 'src/services/core/toast/toast.service';
import { ContactService } from 'src/services/request/contact/contact.service';
import { ContactRequestModel } from 'src/models/requestModels/contact/contactRequestModel';
import { Config } from 'src/infrastructure/config';

@Component({
  selector: 'app-pickupoffice',
  templateUrl: './pickupoffice.component.html',
  styleUrls: ['./pickupoffice.component.css']
})
export class PickupofficeComponent extends HistoryBaseComponent implements OnInit, OnDestroy {

  private static readonly DEFAULT_CITY_KEY = 68134;
  private static readonly DEFAULT_CITY_NAME = 'гр.София';

  private _cityKey = 0;
  private _loggedUser?: UserDTO;
  private _pickupInfo: FormGroup;
  private _cities: CarrierAddressItem = { data: { results: [] } };
  private _offices: SpeedyOffice = { data: { results: [] } };
  private _allOffices: SpeedyOfficeItem[] = [];
  private _order?: OrderInfo;

  private readonly destroy$ = new Subject<void>();
  private readonly citySearch$ = new Subject<string>();

  public city = '';
  public pickupOffice = '';
  public officeSearch = '';
  public showCityResults = false;
  public cityLoading = false;
  public officesLoading = false;
  public selectedOfficeId?: number;

  constructor(
    private FB: FormBuilder,
    private loggedUserService: LoggedUserService,
    private router: Router,
    private toastr: ToastService,
    private orderStateService: OrederStateService,
    private speedyService: SpeedyService,
    private contactsService: ContactService,
    protected location: Location,
    protected historyService: HistoryService
  ) {
    super(location, historyService);

    this._pickupInfo = FB.group({
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      phone: ['', [Validators.required]],
      city: ['', [Validators.required]],
      country: ['България', [Validators.required]],
      pickupOffice: ['', [Validators.required]]
    });

    this.configureCitySearch();

    this.loggedUserService.userInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => this._loggedUser = user);

    this.orderStateService.orderInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orderInfo => {
        this._order = orderInfo;
        this.setCurrentPickUpOffice();
      });
  }

  ngOnInit(): void {
    if (!this._order?.carrierOfficeAddress) {
      this.setDefaultCity();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pickupInfo(): FormGroup {
    return this._pickupInfo;
  }

  get cities(): CarrierAddressItem {
    return this._cities;
  }

  get offices(): SpeedyOffice {
    return this._offices;
  }

  get hasSelectedCity(): boolean {
    return this._cityKey > 0;
  }

  private configureCitySearch(): void {
    this.citySearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(query => this.loadCitiesStore(query));
  }

  private cleanQuery(query: string): string {
    return (query || '').replace(/гр\.|с\./g, '').trim();
  }

  private setCurrentPickUpOffice(): void {
    if (!this._order?.carrierOfficeAddress) {
      return;
    }

    const [firstName = '', lastName = ''] = (this._order.carrierOfficeName || '').split(' ');

    this._pickupInfo.patchValue({
      first_name: firstName,
      last_name: lastName,
      phone: this._order.carrierOfficePhone || '',
      city: this._order.carrierOfficeCity || '',
      pickupOffice: this._order.carrierOfficeAddress
    });

    this._cityKey = this._order.carrierCityKey || 0;
    this.city = this._order.carrierOfficeCity || '';
    this.pickupOffice = this._order.carrierOfficeAddress;
    this.selectedOfficeId = this._order.carrierOfficeId;

    if (this._cityKey) {
      this.loadOfficesForSelectedCity();
    }
  }

  private loadCitiesStore(cityQuery: string): void {
    const query = this.cleanQuery(cityQuery);

    if (!query) {
      this._cities = { data: { results: [] } };
      this.cityLoading = false;
      return;
    }

    this.cityLoading = true;
    this.speedyService.searchCarrierCity(100, query)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        cities => {
          this._cities = cities;
          this.cityLoading = false;
        },
        () => {
          this._cities = { data: { results: [] } };
          this.cityLoading = false;
          this.toastr.error('Не успяхме да заредим населените места. Опитайте отново.');
        }
      );
  }

  private setDefaultCity(): void {
    this._cityKey = PickupofficeComponent.DEFAULT_CITY_KEY;
    this.city = PickupofficeComponent.DEFAULT_CITY_NAME;
    this._pickupInfo.get('city')?.setValue(this.city);
    this.loadOfficesForSelectedCity();
  }

  private loadOfficesForSelectedCity(): void {
    if (!this._cityKey) {
      return;
    }

    const requestedCityKey = this._cityKey;

    this.officesLoading = true;
    this.speedyService.searchCarrierPickUpOffice(requestedCityKey.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        offices => {
          if (this._cityKey !== requestedCityKey) {
            return;
          }

          this._allOffices = offices.data.results || [];
          this.filterOffices(this.officeSearch);
          this.officesLoading = false;
        },
        () => {
          if (this._cityKey !== requestedCityKey) {
            return;
          }

          this._allOffices = [];
          this._offices = { data: { results: [] } };
          this.officesLoading = false;
          this.toastr.error('Не успяхме да заредим офисите на Speedy. Опитайте отново.');
        }
      );
  }

  public onCityInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.city = value;
    this.showCityResults = !!this.cleanQuery(value);

    if (this._cityKey) {
      this._cityKey = 0;
      this._pickupInfo.get('city')?.reset('');
      this.resetOffice();
    }

    const query = this.cleanQuery(value);
    if (!query) {
      this._cities = { data: { results: [] } };
      return;
    }

    this.citySearch$.next(query);
  }

  public selectCity(city: CarrierAddressEntry): void {
    this._cityKey = city.key;
    this.city = city.value;
    this.showCityResults = false;
    this._pickupInfo.get('city')?.setValue(city.value);
    this._pickupInfo.get('city')?.markAsTouched();

    this.resetOffice();
    this.loadOfficesForSelectedCity();
  }

  public onOfficeSearch(event: Event): void {
    this.officeSearch = (event.target as HTMLInputElement).value;
    this.filterOffices(this.officeSearch);
  }

  private filterOffices(search: string): void {
    const normalizedSearch = this.normalizeText(search);
    const availableOffices = this._allOffices.filter(office =>
      (office.type || '').toUpperCase() !== 'APT'
    );

    const results = !normalizedSearch
      ? availableOffices
      : availableOffices.filter(office => {
          const searchableText = [
            office.name,
            office.address?.fullAddressString,
            office.address?.siteAddressString,
            office.address?.localAddressString
          ]
            .filter(Boolean)
            .map(value => this.normalizeText(value as string))
            .join(' ');

          return searchableText.includes(normalizedSearch);
        });

    this._offices = { data: { results } };
  }

  private normalizeText(value: string): string {
    return (value || '').toLocaleLowerCase('bg-BG').trim();
  }

  public selectOffice(office: SpeedyOfficeItem): void {
    this.selectedOfficeId = office.id;
    this.pickupOffice = office.address.fullAddressString;

    if (this._order) {
      this._order.carrierOfficeId = office.id;
    }

    this._pickupInfo.get('pickupOffice')?.setValue(office.address.fullAddressString);
    this._pickupInfo.get('pickupOffice')?.markAsTouched();
  }

  public resetOffice(): void {
    this.pickupOffice = '';
    this.officeSearch = '';
    this.selectedOfficeId = undefined;
    this._allOffices = [];
    this._offices = { data: { results: [] } };
    this._pickupInfo.get('pickupOffice')?.reset('');

    if (this._order) {
      this._order.carrierOfficeId = undefined;
    }
  }

  public trackOfficeById(index: number, office: SpeedyOfficeItem): number {
    return office.id;
  }

  public choose(): void {
    this._order!.toCarrierOffice = true;
    this._order!.toAddress = false;
    this._order!.carrierOfficeAddress = this._pickupInfo.get('pickupOffice')?.value;
    this._order!.carrierOfficeName = this._pickupInfo.get('first_name')?.value + ' ' + this._pickupInfo.get('last_name')?.value;
    this._order!.carrierOfficePhone = this._pickupInfo.get('phone')?.value;
    this._order!.carrierOfficeCity = this._pickupInfo.get('city')?.value;
    this._order!.carrierCityKey = this._cityKey || 0;
    this.orderStateService.addNewOrderInfo(this._order!);
    this.goToOrder();
  }

  public goToOrder(): void {
    this.router.navigate(['order/details']);
  }

  public choosePickup(): void {
    const contactRequestModel: ContactRequestModel = {
      country_uuid: 'fee09419-17d4-4ec0-bf7d-a3b12b5a5cc9',
      contactable_type: Config.user,
      contactable_uuid: this._loggedUser!.data.uuid,
      type: Config.userOfficeLocationRole,
      city: this._pickupInfo.get('city')?.value,
      contact_person_first_name: this._pickupInfo.get('first_name')?.value,
      contact_person_last_name: this._pickupInfo.get('last_name')?.value,
      postcode: '0000',
      street_name: '-',
      street_number: 0,
      address_line_1: this._pickupInfo.get('pickupOffice')?.value,
      address_line_2: this._pickupInfo.get('pickupOffice')?.value,
      mobile_phone_1: this._pickupInfo.get('phone')?.value,
      mobile_phone_1_country_code: 'BG',
      name: this.getPersonalName()
    };

    this.contactsService.registerUserContact(contactRequestModel).subscribe((createdContact: any) => {
      this._order!.contactUUID = createdContact.data.uuid;
      this.choose();
    });
  }

  private getPersonalName(): string {
    if (this._pickupInfo.get('first_name')?.value && this._pickupInfo.get('last_name')?.value) {
      return this._pickupInfo.get('first_name')?.value + ' ' + this._pickupInfo.get('last_name')?.value;
    }
    return '';
  }
}
